import { useState, useEffect } from 'react'
import moment from 'moment'

import {
  formatDateTime,
  formatNumber,
  formatCurrency,
  formatPhoneNumber,
  capitalizeString,
  formatString,
  formatTicketGroups,
  createWorkbook,
  addWorksheet,
  DEFAULTNUMHEADERS,
  saveWorkbook,
  setHeaders,
  setColumnsAndRows,
  numberFormat,
  currencyFormat,
} from '../../utilities/helpers'

import { getPackagesReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'

export default function PackagesReportWrapper({ organization }) {
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [packagesData, setPackagesData] = useState(null)

  const [fromDate, setFromDate] = useState(null)

  const [toDate, setToDate] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || !toDate) return

      setIsLoadingData(true)

      try {
        const res = await getPackagesReport(
          organization.uuid,
          fromDate?.toISOString(),
          toDate?.toISOString()
        )

        setPackagesData(res.data)
      } catch (error) {
        console.error('Error fetching packages data:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    fetchData()
  }, [fromDate, toDate, organization.uuid, refreshToken])

  const handleExcelExport = async (
    organizationName,
    toDate,
    fromDate,
    reportData
  ) => {
    const dateRange = `${formatDateTime(moment(fromDate), 'dateOnly')} - ${formatDateTime(moment(toDate), 'dateOnly')}`

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Packages', workbook)

    const format = (order) => {
      const {
        packageName,
        packageDate,
        packageId,
        orderId,
        orderDate,
        organization,
        firstName,
        lastName,
        email,
        phoneNumber,
        buyerType,
        salesChannel,
        tickets,
        quantity,
        basePrice,
        serviceFee,
        salesTax,
        processingFee,
        grossSales,
        netSales,
        paymentMethod,
        brand,
      } = order

      return {
        name_of_package: packageName,
        package_date: packageDate,
        package_id: packageId,
        order_id: orderId,
        order_date: orderDate,
        first_name: firstName ? capitalizeString(firstName) : 'N/A',
        last_name: lastName ? capitalizeString(lastName) : 'N/A',
        email: email ?? 'N/A',
        phone_number: formatPhoneNumber(phoneNumber),
        organization,
        buyer_type: buyerType ?? 'N/A',
        sales_channel: formatString(salesChannel),
        section: [
          ...new Set(
            formatTicketGroups(tickets)?.map(
              (group) => group?.sectionNumber ?? 'N/A'
            )
          ),
        ].join(', '),
        row: [
          ...new Set(
            formatTicketGroups(tickets)?.map(
              (group) => group?.rowNumber ?? 'N/A'
            )
          ),
        ].join(', '),
        seats: formatTicketGroups(tickets)
          ?.map((group) => group?.seats ?? 'N/A')
          .join(', '),
        qty: quantity,
        base_price: parseFloat(basePrice),
        service_fee: serviceFee,
        sales_tax: salesTax,
        processing_fee: processingFee,
        gross_sales: grossSales,
        net_sales: netSales,
        payment_method: paymentMethod ? capitalizeString(paymentMethod) : 'N/A',
        brand: brand ? capitalizeString(brand) : 'N/A',
      }
    }

    const headers = [`Date Range: ${dateRange}`]

    setHeaders('Packages', organizationName, worksheet, headers)

    setColumnsAndRows(
      Object.keys(format(reportData)),
      Object.values(reportData),
      format,
      worksheet
    )

    const headerRows = DEFAULTNUMHEADERS + headers?.length

    numberFormat(16, worksheet, headerRows)
    currencyFormat(17, worksheet, headerRows)
    currencyFormat(18, worksheet, headerRows)
    currencyFormat(19, worksheet, headerRows)
    currencyFormat(20, worksheet, headerRows)
    currencyFormat(21, worksheet, headerRows)
    currencyFormat(22, worksheet, headerRows)

    // Generate and download Excel file
    // save excel worksheet
    saveWorkbook(workbook, 'packages report')
      .then()
      .catch((err) => alert(err.message))
  }

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  return (
    <>
      <section className="spacer-sm">
        <header className="section-header-sm">
          <div className="section-heading">
            <h1>Packages Report</h1>
          </div>
          <p className="section-header-desc">
            View all season and flex package sales, quantities, and revenue
            within the selected date range.
          </p>
        </header>
        <ReportActions
          isLoading={isLoadingData}
          onRefresh={refresh}
          isRefreshing={isLoadingData}
          fromDate={fromDate}
          toDate={toDate}
          data={packagesData}
          onDateRangeChange={handleDateRangeChange}
          downloadReport={() =>
            handleExcelExport(
              organization?.name,
              toDate,
              fromDate,
              packagesData
            )
          }
        />
        <Results
          isLoading={isLoadingData}
          fromDate={fromDate}
          toDate={toDate}
          data={packagesData}
        />
      </section>
    </>
  )
}

const PackageData = ({ data }) => {
  const ticketGroups = formatTicketGroups(data?.tickets)

  return (
    <tr className="md-x md-x--scroll">
      <td>{data?.packageName}</td>
      <td className="text-left">{data?.packageDate ?? 'N/A'}</td>
      <td className="text-left">{data?.packageId ?? 'N/A'}</td>
      <td className="text-left">{data?.orderId}</td>
      <td className="text-left">{data?.orderDate ?? 'N/A'}</td>
      <td className="text-left">
        {data?.firstName ? capitalizeString(data?.firstName) : 'N/A'}
      </td>
      <td className="text-left">
        {data?.lastName ? capitalizeString(data?.lastName) : 'N/A'}
      </td>
      <td className="text-left">{data?.email ?? 'N/A'}</td>
      <td className="text-left">{formatPhoneNumber(data?.phoneNumber)}</td>
      <td className="text-left">{data?.organization ?? 'N/A'}</td>
      <td className="text-left">{data?.buyerType ?? 'N/A'}</td>
      <td className="text-left">{formatString(data.salesChannel)}</td>
      <td className="text-left">
        {[
          ...new Set(
            ticketGroups?.map((group) => group?.sectionNumber ?? 'N/A')
          ),
        ].join(', ')}
      </td>
      <td className="text-left">
        {[
          ...new Set(ticketGroups?.map((group) => group?.rowNumber ?? 'N/A')),
        ].join(', ')}
      </td>
      <td className="text-left">
        {ticketGroups?.map((group) => group?.seats ?? 'N/A').join(', ')}
      </td>
      <td className="text-left">{formatNumber(data?.quantity)}</td>
      <td className="text-left">{formatCurrency(data?.basePrice)}</td>
      <td className="text-left">{formatCurrency(data?.serviceFee)}</td>
      <td className="text-left">{formatCurrency(data?.salesTax)}</td>
      <td className="text-left">{formatCurrency(data?.processingFee)}</td>
      <td className="text-left">{formatCurrency(data?.grossSales)}</td>
      <td className="text-left">{formatCurrency(data?.netSales)}</td>
      <td className="text-left">
        {data?.paymentMethod ? capitalizeString(data?.paymentMethod) : 'N/A'}
      </td>
      <td>{data?.brand ? capitalizeString(data?.brand) : 'N/A'}</td>
    </tr>
  )
}

// Shared table headers
const TableHeader = () => (
  <thead>
    <tr className="md md--scroll">
      <th className="uppercase text-body">Package name</th>
      <th className="text-left uppercase text-body">Package date/time</th>
      <th className="text-left uppercase text-body">Package id</th>
      <th className="text-left uppercase text-body">Order id</th>
      <th className="text-left uppercase text-body">Order date</th>
      <th className="text-left uppercase text-body">First name</th>
      <th className="text-left uppercase text-body">Last name</th>
      <th className="text-left uppercase text-body">Email</th>
      <th className="text-left uppercase text-body">Phone number</th>
      <th className="text-left uppercase text-body">Organization</th>
      <th className="text-left uppercase text-body">Buyer type</th>
      <th className="text-left uppercase text-body">Sales channel</th>
      <th className="text-left uppercase text-body">Section</th>
      <th className="text-left uppercase text-body">Row</th>
      <th className="text-left uppercase text-body">Seats</th>
      <th className="text-left uppercase text-body">Qty</th>
      <th className="text-left uppercase text-body">Base price</th>
      <th className="text-left uppercase text-body">Service fee</th>
      <th className="text-left uppercase text-body">Sales tax</th>
      <th className="text-left uppercase text-body">Processing fee</th>
      <th className="text-left uppercase text-body">Gross sales</th>
      <th className="text-left uppercase text-body">Net sales</th>
      <th className="uppercase text-body">Payment method</th>
      <th className="uppercase text-body">Brand</th>
    </tr>
  </thead>
)

function PackagesReportTable({ packagesData }) {
  return (
    <div className="table-container">
      <div className="table-container--fixed-height--scrollable">
        <Table responsive>
          <TableHeader />
          <tbody>
            {packagesData?.map((data, index) => (
              <PackageData key={index} data={data} />
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

function Results({ isLoading, data, fromDate, toDate }) {
  if (!fromDate || !toDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select an event, season or date range from the options above. Once
            you've made your selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!data || data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for the specified date range. Please
            select a different date range.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <PackagesReportTable packagesData={data} />
    </Card>
  )
}
